import { Facebook, Instagram, Mail, MapPin, Phone, Youtube } from "lucide-react";
import { MaskedLine, Reveal } from "./motion";

const TikTokIcon = ({ size = 20 }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
);

const SOCIALS = [
    {
        icon: Instagram,
        label: "Instagram",
        href: "https://www.instagram.com/",
        testId: "social-instagram",
    },
    {
        icon: Youtube,
        label: "YouTube",
        href: "https://www.youtube.com/",
        testId: "social-youtube",
    },
    {
        icon: TikTokIcon,
        label: "TikTok",
        href: "https://www.tiktok.com/",
        testId: "social-tiktok",
    },
    {
        icon: Facebook,
        label: "Facebook",
        href: "https://www.facebook.com/",
        testId: "social-facebook",
    },
];

const CONTACTS = [
    {
        icon: Mail,
        title: "COURRIEL",
        value: "photographedanieldulude@gmail.com",
        href: "https://mail.google.com/mail/?view=cm&fs=1&to=photographedanieldulude@gmail.com",
        newTab: true,
        testId: "contact-email-link",
    },
    {
        icon: Phone,
        title: "TÉLÉPHONE",
        value: "514 781-8360",
        href: "tel:+15147818360",
        testId: "contact-phone-link",
    },
    {
        icon: MapPin,
        title: "RÉGION",
        value: "Saint-Jean-sur-Richelieu, Québec — Montérégie",
        href: null,
        testId: "contact-location",
    },
];

export default function Contact() {
    return (
        <section
            id="contact"
            data-testid="section-contact"
            className="relative flex min-h-[92vh] items-center overflow-hidden border-t border-[#D4AF37]/15 py-24 sm:py-32"
        >
            <div className="glow-pulse pointer-events-none absolute left-1/2 top-1/2 h-[80vh] w-[90vw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.14),transparent_60%)]" />
            <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_bottom,#050505,transparent_25%,transparent_75%,#050505)]" />
            <div className="pointer-events-none absolute inset-y-0 left-1/2 hidden w-px bg-gradient-to-b from-transparent via-[#D4AF37]/20 to-transparent lg:block" />

            <div className="relative z-10 mx-auto w-full max-w-[1200px] px-5 text-center sm:px-10">
                <Reveal>
                    <p className="font-mono-lux mb-8 flex items-center justify-center gap-4 text-[11px] tracking-[0.4em] text-[#D4AF37]">
                        <span className="inline-block h-px w-10 bg-[#D4AF37]/60" />
                        CONTACT
                        <span className="inline-block h-px w-10 bg-[#D4AF37]/60" />
                    </p>
                </Reveal>

                <h2
                    data-testid="contact-title"
                    className="font-display text-4xl font-medium leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-7xl"
                >
                    <MaskedLine delay={0.1}>VOTRE HISTOIRE MÉRITE</MaskedLine>
                    <MaskedLine delay={0.25}>
                        <span className="text-gold-grad italic">
                            D'ÊTRE EN IMAGE
                        </span>
                    </MaskedLine>
                </h2>

                <Reveal delay={0.35}>
                    <p className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-[#E2E2E2]/85 sm:text-lg">
                        Communiquez avec moi pour discuter de votre projet
                        photographique et créer ensemble des images qui vous
                        ressemblent.
                    </p>
                </Reveal>

                <div className="mx-auto mt-16 grid max-w-5xl gap-5 sm:grid-cols-3">
                    {CONTACTS.map((c, i) => (
                        <Reveal key={c.title} delay={0.4 + i * 0.12}>
                            <div
                                data-testid={c.testId}
                                className="group flex h-full flex-col items-center gap-4 border border-[#D4AF37]/30 bg-[#0B0B0C]/70 px-6 py-9 backdrop-blur-sm transition-colors duration-500 hover:border-[#D4AF37]/70"
                            >
                                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/40 text-[#D4AF37] transition-transform duration-500 group-hover:scale-110">
                                    <c.icon size={20} strokeWidth={1.4} />
                                </span>
                                <span className="font-mono-lux text-[11px] tracking-[0.3em] text-[#D4AF37]">
                                    {c.title}
                                </span>
                                {c.href ? (
                                    <a
                                        href={c.href}
                                        {...(c.newTab
                                            ? {
                                                  target: "_blank",
                                                  rel: "noopener noreferrer",
                                              }
                                            : {})}
                                        className="break-all text-xs font-light leading-relaxed text-[#E2E2E2] transition-colors duration-300 hover:text-[#F3E5AB] sm:text-[13px]"
                                    >
                                        {c.value}
                                    </a>
                                ) : (
                                    <span className="text-sm font-light leading-relaxed text-[#E2E2E2]">
                                        {c.value}
                                    </span>
                                )}
                            </div>
                        </Reveal>
                    ))}
                </div>

                <Reveal delay={0.75}>
                    <div
                        data-testid="contact-socials"
                        className="mt-14 flex flex-col items-center gap-6"
                    >
                        <p className="font-mono-lux flex items-center gap-4 text-[10px] tracking-[0.4em] text-[#8A8A8E]">
                            <span className="inline-block h-px w-10 bg-[#D4AF37]/40" />
                            SUIVEZ-MOI
                            <span className="inline-block h-px w-10 bg-[#D4AF37]/40" />
                        </p>
                        <div className="flex items-center gap-5">
                            {SOCIALS.map((s) => (
                                <a
                                    key={s.label}
                                    data-testid={s.testId}
                                    href={s.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={s.label}
                                    title={s.label}
                                    className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/30 text-[#D4AF37]/80 transition-all duration-500 hover:-translate-y-1 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 hover:text-[#F3E5AB] hover:shadow-[0_0_25px_rgba(212,175,55,0.2)]"
                                >
                                    <s.icon size={19} strokeWidth={1.4} />
                                </a>
                            ))}
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    );
}
